import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Package, Truck, MapPin, Car, Clock, CheckCircle,
  AlertTriangle, RefreshCw, Plus, ArrowLeft, Loader2,
  Navigation, Clipboard, Phone, Building, Home,
  Bed, Wind, Armchair, ShowerHead, UtensilsCrossed, Filter as FilterIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  beds: <Bed className="w-4 h-4" />,
  oxygen: <Wind className="w-4 h-4" />,
  mobility: <Armchair className="w-4 h-4" />,
  bathroom: <ShowerHead className="w-4 h-4" />,
  nutrition: <UtensilsCrossed className="w-4 h-4" />,
  suction: <FilterIcon className="w-4 h-4" />,
  wound_care: <Package className="w-4 h-4" />
};

export default function OperationsManagement() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [dmeCatalog, setDmeCatalog] = useState({});
  const [dmeVendors, setDmeVendors] = useState([]);
  const [dmeOrders, setDmeOrders] = useState([]);
  const [supplyOrders, setSupplyOrders] = useState([]);
  const [evvRecords, setEvvRecords] = useState([]);
  const [mileageRecords, setMileageRecords] = useState({ records: [], summary: {} });
  const [charts, setCharts] = useState([]);
  const [selectedChart, setSelectedChart] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showDmeOrderDialog, setShowDmeOrderDialog] = useState(false);
  const [showMileageDialog, setShowMileageDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('beds');
  
  const token = localStorage.getItem('hospice_token');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/operations/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error('Error fetching operations dashboard:', error);
    }
  }, [token]);

  const fetchDmeCatalog = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/operations/dme/catalog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setDmeCatalog(data.catalog || {});
    } catch (error) {
      console.error('Error fetching DME catalog:', error);
    }
  }, [token]);

  const fetchDmeVendors = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/operations/dme/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setDmeVendors(data.vendors || []);
    } catch (error) {
      console.error('Error fetching DME vendors:', error);
    }
  }, [token]);

  const fetchDmeOrders = useCallback(async () => {
    try {
      const url = selectedChart 
        ? `${API_URL}/api/ehr/operations/dme/orders?chart_id=${selectedChart}` 
        : `${API_URL}/api/ehr/operations/dme/orders`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setDmeOrders(data.orders || []);
    } catch (error) {
      console.error('Error fetching DME orders:', error);
    }
  }, [token, selectedChart]);

  const fetchSupplyOrders = useCallback(async () => {
    try {
      const url = selectedChart 
        ? `${API_URL}/api/ehr/operations/supplies/orders?chart_id=${selectedChart}` 
        : `${API_URL}/api/ehr/operations/supplies/orders`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSupplyOrders(data.orders || []);
    } catch (error) {
      console.error('Error fetching supply orders:', error);
    }
  }, [token, selectedChart]);

  const fetchEvvRecords = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(`${API_URL}/api/ehr/operations/evv/records?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setEvvRecords(data.records || []);
    } catch (error) {
      console.error('Error fetching EVV records:', error);
    }
  }, [token]);

  const fetchMileageRecords = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/operations/mileage/records?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setMileageRecords(data);
    } catch (error) {
      console.error('Error fetching mileage records:', error);
    }
  }, [token]);

  const fetchCharts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/charts?status=active&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCharts(data.charts || []);
    } catch (error) {
      console.error('Error fetching charts:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchDmeCatalog(),
        fetchDmeVendors(),
        fetchDmeOrders(),
        fetchEvvRecords(),
        fetchMileageRecords(),
        fetchCharts()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [fetchDashboard, fetchDmeCatalog, fetchDmeVendors, fetchDmeOrders, fetchEvvRecords, fetchMileageRecords, fetchCharts]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchDashboard(),
      fetchDmeOrders(),
      fetchEvvRecords(),
      fetchMileageRecords()
    ]);
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  const handleCreateDmeOrder = async (orderData) => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/operations/dme/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(orderData)
      });
      const data = await res.json();
      if (data.success) {
        toast.success('DME order created successfully');
        fetchDmeOrders();
        setShowDmeOrderDialog(false);
      }
    } catch (error) {
      toast.error('Failed to create DME order');
    }
  };

  const handleUpdateDmeStatus = async (orderId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/operations/dme/orders/${orderId}/status?status=${status}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Status updated to ${status}`);
        fetchDmeOrders();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleRecordMileage = async (mileageData) => {
    try {
      const params = new URLSearchParams(mileageData).toString();
      const res = await fetch(`${API_URL}/api/ehr/operations/mileage/record?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Mileage recorded: $${data.trip_reimbursement.toFixed(2)} reimbursement`);
        fetchMileageRecords();
        setShowMileageDialog(false);
      }
    } catch (error) {
      toast.error('Failed to record mileage');
    }
  };

  const handleApproveMileage = async (mileageId) => {
    try {
      const res = await fetch(`${API_URL}/api/ehr/operations/mileage/${mileageId}/approve?approved_by=admin`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Mileage approved');
        fetchMileageRecords();
      }
    } catch (error) {
      toast.error('Failed to approve mileage');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
      </div>
    );
  }

  const summary = dashboard?.summary || {};

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="operations-management-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-slate-900 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Package className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Operations Management</h1>
                <p className="text-slate-400 text-sm">DME, Supplies, EVV & Mileage Tracking</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/hospice-ehr/command-center'}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Command Center
              </Button>
              <Button 
                variant="outline" 
                onClick={handleRefresh}
                disabled={refreshing}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Dashboard Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <StatCard
            icon={<Package className="w-5 h-5" />}
            label="DME Pending"
            value={summary.dme_pending || 0}
            color="blue"
          />
          <StatCard
            icon={<Truck className="w-5 h-5" />}
            label="DME Shipped"
            value={summary.dme_shipped || 0}
            color="cyan"
          />
          <StatCard
            icon={<MapPin className="w-5 h-5" />}
            label="EVV Today"
            value={summary.evv_visits_today || 0}
            color="purple"
          />
          <StatCard
            icon={<CheckCircle className="w-5 h-5" />}
            label="EVV Verified"
            value={summary.evv_verified || 0}
            color="green"
          />
          <StatCard
            icon={<AlertTriangle className="w-5 h-5" />}
            label="EVV Flagged"
            value={summary.evv_flagged || 0}
            color="orange"
          />
          <StatCard
            icon={<Car className="w-5 h-5" />}
            label="Pending Reimb."
            value={`$${(summary.pending_reimbursement || 0).toFixed(0)}`}
            color="emerald"
          />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-slate-800 border border-slate-700 w-full justify-start mb-4">
            <TabsTrigger value="dme" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-300">
              <Package className="w-4 h-4 mr-2" /> DME Orders
            </TabsTrigger>
            <TabsTrigger value="evv" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-300">
              <MapPin className="w-4 h-4 mr-2" /> EVV Tracking
            </TabsTrigger>
            <TabsTrigger value="mileage" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
              <Car className="w-4 h-4 mr-2" /> Mileage
            </TabsTrigger>
            <TabsTrigger value="catalog" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-300">
              <Clipboard className="w-4 h-4 mr-2" /> DME Catalog
            </TabsTrigger>
          </TabsList>

          {/* DME Orders Tab */}
          <TabsContent value="dme">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-white">DME Orders</CardTitle>
                    <div className="flex gap-2">
                      <Select value={selectedChart} onValueChange={setSelectedChart}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white w-48">
                          <SelectValue placeholder="Filter by patient..." />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="" className="text-white">All Patients</SelectItem>
                          {charts.map((chart) => (
                            <SelectItem key={chart.id} value={chart.id} className="text-white">
                              {chart.demographics?.first_name} {chart.demographics?.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        size="sm" 
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => setShowDmeOrderDialog(true)}
                      >
                        <Plus className="w-4 h-4 mr-1" /> New Order
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {dmeOrders.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">No DME orders found</p>
                  ) : (
                    <div className="max-h-96 overflow-y-auto space-y-3">
                      {dmeOrders.map((order) => (
                        <div key={order.order_id} className="bg-slate-700/50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-white">{order.patient_name || 'Unknown Patient'}</span>
                            <Badge className={
                              order.delivery_status === 'delivered' ? 'bg-green-500/20 text-green-300' :
                              order.delivery_status === 'shipped' ? 'bg-cyan-500/20 text-cyan-300' :
                              order.delivery_status === 'ordered' ? 'bg-blue-500/20 text-blue-300' :
                              order.delivery_status === 'cancelled' ? 'bg-red-500/20 text-red-300' :
                              'bg-yellow-500/20 text-yellow-300'
                            }>
                              {order.delivery_status}
                            </Badge>
                          </div>
                          <div className="text-sm text-slate-400 mb-2">
                            {order.items?.length || 0} item(s) • Ordered: {new Date(order.order_date).toLocaleDateString()}
                          </div>
                          <div className="flex flex-wrap gap-1 mb-3">
                            {order.items?.slice(0, 3).map((item, idx) => (
                              <Badge key={idx} variant="outline" className="border-slate-600 text-slate-300 text-xs">
                                {item.name}
                              </Badge>
                            ))}
                            {order.items?.length > 3 && (
                              <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">
                                +{order.items.length - 3} more
                              </Badge>
                            )}
                          </div>
                          {order.delivery_status !== 'delivered' && order.delivery_status !== 'cancelled' && (
                            <div className="flex gap-2">
                              {order.delivery_status === 'pending' && (
                                <Button 
                                  size="sm" 
                                  className="bg-blue-600 hover:bg-blue-700"
                                  onClick={() => handleUpdateDmeStatus(order.order_id, 'ordered')}
                                >
                                  Mark Ordered
                                </Button>
                              )}
                              {order.delivery_status === 'ordered' && (
                                <Button 
                                  size="sm" 
                                  className="bg-cyan-600 hover:bg-cyan-700"
                                  onClick={() => handleUpdateDmeStatus(order.order_id, 'shipped')}
                                >
                                  Mark Shipped
                                </Button>
                              )}
                              {order.delivery_status === 'shipped' && (
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleUpdateDmeStatus(order.order_id, 'delivered')}
                                >
                                  Mark Delivered
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-white">DME Vendors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dmeVendors.map((vendor) => (
                      <div key={vendor.vendor_id} className="bg-slate-700/50 rounded-lg p-3">
                        <p className="font-medium text-white">{vendor.name}</p>
                        <div className="flex items-center justify-between mt-1">
                          <Badge className="bg-slate-600 text-slate-300 text-xs">{vendor.type}</Badge>
                          <a 
                            href={`tel:${vendor.phone}`} 
                            className="flex items-center gap-1 text-blue-400 text-sm hover:underline"
                          >
                            <Phone className="w-3 h-3" />
                            {vendor.phone}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* EVV Tab */}
          <TabsContent value="evv">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-purple-400" />
                  Today's Visit Verifications
                </CardTitle>
                <CardDescription className="text-slate-400">
                  GPS-verified visit check-ins and check-outs
                </CardDescription>
              </CardHeader>
              <CardContent>
                {evvRecords.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">No EVV records for today</p>
                ) : (
                  <div className="space-y-3">
                    {evvRecords.map((record) => (
                      <div key={record.verification_id} className="bg-slate-700/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-medium text-white">{record.staff_name || 'Staff'}</span>
                            <span className="text-slate-400 mx-2">→</span>
                            <span className="text-slate-300">{record.patient_address || 'Patient'}</span>
                          </div>
                          <Badge className={
                            record.verification_status === 'verified' ? 'bg-green-500/20 text-green-300' :
                            record.verification_status === 'flagged' ? 'bg-amber-500/20 text-amber-300' :
                            record.verification_status === 'exception' ? 'bg-red-500/20 text-red-300' :
                            'bg-blue-500/20 text-blue-300'
                          }>
                            {record.verification_status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-slate-400">Check-in</p>
                            <p className="text-white">
                              {record.actual_start ? new Date(record.actual_start).toLocaleTimeString() : 'N/A'}
                            </p>
                            {record.distance_from_patient_at_checkin > 0 && (
                              <p className={`text-xs ${record.location_verified ? 'text-green-400' : 'text-amber-400'}`}>
                                {Math.round(record.distance_from_patient_at_checkin)}m from patient
                              </p>
                            )}
                          </div>
                          <div>
                            <p className="text-slate-400">Check-out</p>
                            <p className="text-white">
                              {record.actual_end ? new Date(record.actual_end).toLocaleTimeString() : 'Pending'}
                            </p>
                            {record.distance_from_patient_at_checkout > 0 && (
                              <p className={`text-xs ${record.distance_from_patient_at_checkout <= 400 ? 'text-green-400' : 'text-amber-400'}`}>
                                {Math.round(record.distance_from_patient_at_checkout)}m from patient
                              </p>
                            )}
                          </div>
                        </div>
                        {record.exception_reason && (
                          <p className="text-red-400 text-sm mt-2">{record.exception_reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mileage Tab */}
          <TabsContent value="mileage">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-white">Mileage Records</CardTitle>
                    <Button 
                      size="sm" 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setShowMileageDialog(true)}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Log Mileage
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {mileageRecords.records.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">No mileage records found</p>
                  ) : (
                    <div className="max-h-96 overflow-y-auto space-y-3">
                      {mileageRecords.records.map((record) => (
                        <div key={record.mileage_id} className="bg-slate-700/50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="font-medium text-white">{record.staff_name}</span>
                              <span className="text-slate-400 mx-2">•</span>
                              <span className="text-slate-300">{record.date}</span>
                            </div>
                            <Badge className={
                              record.status === 'approved' ? 'bg-green-500/20 text-green-300' :
                              record.status === 'paid' ? 'bg-emerald-500/20 text-emerald-300' :
                              record.status === 'submitted' ? 'bg-blue-500/20 text-blue-300' :
                              'bg-yellow-500/20 text-yellow-300'
                            }>
                              {record.status}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-slate-400">
                              {record.trips?.length || 0} trip(s) • {record.total_miles?.toFixed(1)} miles
                            </div>
                            <div className="text-emerald-400 font-bold">
                              ${record.total_reimbursement?.toFixed(2)}
                            </div>
                          </div>
                          {record.status === 'submitted' && (
                            <Button 
                              size="sm" 
                              className="mt-2 bg-green-600 hover:bg-green-700"
                              onClick={() => handleApproveMileage(record.mileage_id)}
                            >
                              Approve
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-white">Mileage Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-400">
                        {mileageRecords.summary.total_miles?.toFixed(1) || 0}
                      </p>
                      <p className="text-slate-400 text-sm">Total Miles</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-green-400">
                        ${mileageRecords.summary.total_reimbursement?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-slate-400 text-sm">Total Reimbursement</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-white">
                        {mileageRecords.summary.record_count || 0}
                      </p>
                      <p className="text-slate-400 text-sm">Records</p>
                    </div>
                    <div className="text-center text-xs text-slate-400">
                      Rate: $0.67/mile (2024 IRS)
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* DME Catalog Tab */}
          <TabsContent value="catalog">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-white">Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.keys(dmeCatalog).map((category) => (
                      <Button
                        key={category}
                        variant={selectedCategory === category ? "default" : "outline"}
                        className={`w-full justify-start ${
                          selectedCategory === category 
                            ? 'bg-blue-600 hover:bg-blue-700' 
                            : 'border-slate-600 text-slate-300 hover:bg-slate-700'
                        }`}
                        onClick={() => setSelectedCategory(category)}
                      >
                        {categoryIcons[category] || <Package className="w-4 h-4" />}
                        <span className="ml-2 capitalize">{category.replace('_', ' ')}</span>
                        <Badge className="ml-auto bg-slate-700 text-slate-300">
                          {dmeCatalog[category]?.length || 0}
                        </Badge>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700 lg:col-span-3">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-white capitalize">
                    {selectedCategory?.replace('_', ' ')} Equipment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {dmeCatalog[selectedCategory]?.map((item) => (
                      <div key={item.item_id} className="bg-slate-700/50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-white">{item.name}</p>
                            <p className="text-sm text-slate-400">HCPCS: {item.hcpcs}</p>
                          </div>
                          <Badge className="bg-blue-500/20 text-blue-300 text-xs">
                            {item.item_id}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* DME Order Dialog */}
      <Dialog open={showDmeOrderDialog} onOpenChange={setShowDmeOrderDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Create DME Order</DialogTitle>
          </DialogHeader>
          <DmeOrderForm 
            charts={charts}
            catalog={dmeCatalog}
            vendors={dmeVendors}
            onSubmit={handleCreateDmeOrder} 
            onCancel={() => setShowDmeOrderDialog(false)} 
          />
        </DialogContent>
      </Dialog>

      {/* Mileage Dialog */}
      <Dialog open={showMileageDialog} onOpenChange={setShowMileageDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Log Mileage</DialogTitle>
          </DialogHeader>
          <MileageForm 
            onSubmit={handleRecordMileage} 
            onCancel={() => setShowMileageDialog(false)} 
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colorClasses = {
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400',
    cyan: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30 text-cyan-400',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
    green: 'from-green-500/20 to-green-600/10 border-green-500/30 text-green-400',
    orange: 'from-orange-500/20 to-orange-600/10 border-orange-500/30 text-orange-400',
    emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 text-emerald-400'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

function DmeOrderForm({ charts, catalog, vendors, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    chart_id: '',
    vendor_id: '',
    vendor_name: '',
    items: [],
    medical_necessity: '',
    notes: ''
  });
  const [selectedCategory, setSelectedCategory] = useState('beds');

  const handleAddItem = (item) => {
    if (!formData.items.find(i => i.item_id === item.item_id)) {
      setFormData({
        ...formData,
        items: [...formData.items, { ...item, quantity: 1 }]
      });
    }
  };

  const handleRemoveItem = (itemId) => {
    setFormData({
      ...formData,
      items: formData.items.filter(i => i.item_id !== itemId)
    });
  };

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.vendor_id === vendorId);
    setFormData({
      ...formData,
      vendor_id: vendorId,
      vendor_name: vendor?.name || ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.chart_id || formData.items.length === 0) {
      toast.error('Please select a patient and at least one item');
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-slate-300">Patient</Label>
        <Select value={formData.chart_id} onValueChange={(v) => setFormData({...formData, chart_id: v})}>
          <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
            <SelectValue placeholder="Select patient..." />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            {charts.map((chart) => (
              <SelectItem key={chart.id} value={chart.id} className="text-white">
                {chart.demographics?.first_name} {chart.demographics?.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-slate-300">Vendor</Label>
        <Select value={formData.vendor_id} onValueChange={handleVendorChange}>
          <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
            <SelectValue placeholder="Select vendor..." />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            {vendors.map((vendor) => (
              <SelectItem key={vendor.vendor_id} value={vendor.vendor_id} className="text-white">
                {vendor.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-slate-300">Select Equipment</Label>
        <div className="flex gap-2 mb-2">
          {Object.keys(catalog).map((cat) => (
            <Button
              key={cat}
              type="button"
              size="sm"
              variant={selectedCategory === cat ? "default" : "outline"}
              className={selectedCategory === cat ? 'bg-blue-600' : 'border-slate-600 text-slate-300'}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat.replace('_', ' ')}
            </Button>
          ))}
        </div>
        <div className="bg-slate-700/50 rounded-lg p-2 max-h-32 overflow-y-auto">
          {catalog[selectedCategory]?.map((item) => (
            <button
              key={item.item_id}
              type="button"
              className="w-full text-left p-2 hover:bg-slate-600 rounded text-sm"
              onClick={() => handleAddItem(item)}
            >
              {item.name}
            </button>
          ))}
        </div>
      </div>

      {formData.items.length > 0 && (
        <div>
          <Label className="text-slate-300">Selected Items ({formData.items.length})</Label>
          <div className="space-y-1">
            {formData.items.map((item) => (
              <div key={item.item_id} className="flex items-center justify-between bg-slate-700/50 rounded p-2">
                <span className="text-sm text-white">{item.name}</span>
                <Button 
                  type="button" 
                  size="sm" 
                  variant="ghost" 
                  className="text-red-400 hover:text-red-300"
                  onClick={() => handleRemoveItem(item.item_id)}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <Label className="text-slate-300">Medical Necessity</Label>
        <Textarea
          value={formData.medical_necessity}
          onChange={(e) => setFormData({...formData, medical_necessity: e.target.value})}
          className="bg-slate-700 border-slate-600 text-white"
          placeholder="Document medical necessity..."
          rows={2}
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1 border-slate-600 text-slate-300">
          Cancel
        </Button>
        <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
          Create Order
        </Button>
      </div>
    </form>
  );
}

function MileageForm({ onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    staff_id: 'staff_001',
    staff_name: '',
    date: new Date().toISOString().split('T')[0],
    from_address: '',
    to_address: '',
    miles: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.staff_name || !formData.miles) {
      toast.error('Please fill all required fields');
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-slate-300">Staff Name</Label>
        <Input
          value={formData.staff_name}
          onChange={(e) => setFormData({...formData, staff_name: e.target.value})}
          className="bg-slate-700 border-slate-600 text-white"
          placeholder="Enter your name"
          required
        />
      </div>
      <div>
        <Label className="text-slate-300">Date</Label>
        <Input
          type="date"
          value={formData.date}
          onChange={(e) => setFormData({...formData, date: e.target.value})}
          className="bg-slate-700 border-slate-600 text-white"
          required
        />
      </div>
      <div>
        <Label className="text-slate-300">From Address</Label>
        <Input
          value={formData.from_address}
          onChange={(e) => setFormData({...formData, from_address: e.target.value})}
          className="bg-slate-700 border-slate-600 text-white"
          placeholder="Starting location"
        />
      </div>
      <div>
        <Label className="text-slate-300">To Address</Label>
        <Input
          value={formData.to_address}
          onChange={(e) => setFormData({...formData, to_address: e.target.value})}
          className="bg-slate-700 border-slate-600 text-white"
          placeholder="Destination"
        />
      </div>
      <div>
        <Label className="text-slate-300">Miles</Label>
        <Input
          type="number"
          step="0.1"
          value={formData.miles}
          onChange={(e) => setFormData({...formData, miles: e.target.value})}
          className="bg-slate-700 border-slate-600 text-white"
          placeholder="Enter miles traveled"
          required
        />
        {formData.miles && (
          <p className="text-xs text-emerald-400 mt-1">
            Est. reimbursement: ${(parseFloat(formData.miles) * 0.67).toFixed(2)}
          </p>
        )}
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1 border-slate-600 text-slate-300">
          Cancel
        </Button>
        <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
          Log Mileage
        </Button>
      </div>
    </form>
  );
}
